"use client";

import Image from "next/image";
import Script from "next/script";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Navigation from "../components/Navigation";
import RandomBackground from "../components/RandomBackground";

type ContactInfo = {
  email: string;
  phoneDisplay: string;
  phoneLink: string;
};

type TurnstileApi = {
  render: (
    container: string | HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      theme?: "light" | "dark";
    },
  ) => string;
  reset: (widgetId?: string) => void;
};

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";
const REVEAL_BUTTON_CLASS =
  "inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/35 px-3 py-1.5 text-xs font-semibold text-white/85 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-60 md:text-sm";

function getTurnstileApi(): TurnstileApi | undefined {
  const runtime = globalThis as typeof globalThis & {
    turnstile?: TurnstileApi;
  };
  return runtime.turnstile;
}

function GlassCard({
  children,
  className = "",
}: Readonly<{ children: React.ReactNode; className?: string }>) {
  return (
    <div
      className={`rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 ${className}`}
    >
      {children}
    </div>
  );
}

export default function SupportPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [revealStatus, setRevealStatus] = useState<
    "idle" | "loading" | "error"
  >("idle");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const captchaContainerRef = useRef<HTMLDivElement | null>(null);
  const captchaWidgetIdRef = useRef<string | null>(null);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const renderCaptcha = useCallback(() => {
    const turnstile = getTurnstileApi();
    if (!TURNSTILE_SITE_KEY || !captchaContainerRef.current || !turnstile) {
      return;
    }

    if (captchaWidgetIdRef.current) {
      return;
    }

    captchaWidgetIdRef.current = turnstile.render(captchaContainerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: "dark",
      callback: (token: string) => {
        setCaptchaToken(token);
        setCaptchaError("");
      },
      "error-callback": () => {
        setCaptchaToken("");
        setCaptchaError(
          "Captcha failed to load. Please refresh and try again.",
        );
      },
      "expired-callback": () => {
        setCaptchaToken("");
      },
    });
  }, []);

  useEffect(() => {
    renderCaptcha();
  }, [renderCaptcha]);

  const resetCaptcha = useCallback(() => {
    const turnstile = getTurnstileApi();
    if (TURNSTILE_SITE_KEY && turnstile && captchaWidgetIdRef.current) {
      turnstile.reset(captchaWidgetIdRef.current);
    }
    setCaptchaToken("");
  }, []);

  const handleRevealSupportEmail = async () => {
    if (contactInfo || revealStatus === "loading") {
      return;
    }

    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setCaptchaError("Please complete the captcha first.");
      return;
    }

    setRevealStatus("loading");
    try {
      const response = await fetch("/api/contact-info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ website: "", captchaToken }),
      });

      if (!response.ok) {
        setRevealStatus("error");
        resetCaptcha();
        return;
      }

      const payload: ContactInfo = await response.json();
      setContactInfo(payload);
      setRevealStatus("idle");
      resetCaptcha();
    } catch {
      setRevealStatus("error");
      resetCaptcha();
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setCaptchaError("Please complete the captcha first.");
      setStatus("error");
      return;
    }

    setStatus("sending");
    try {
      const response = await fetch("/api/contact-submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          subject: "Support Request",
          message: form.message,
          website: "",
          captchaToken,
        }),
      });

      if (response.ok) {
        setStatus("sent");
        setForm({ name: "", email: "", message: "" });
        resetCaptcha();
        return;
      }
      setStatus("error");
      resetCaptcha();
    } catch {
      setStatus("error");
      resetCaptcha();
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden text-white">
      <Navigation />
      <RandomBackground />
      <div
        aria-hidden
        className="fixed inset-0 -z-10 bg-gradient-to-b from-black/75 via-black/25 to-black/10"
      />
      <div
        aria-hidden
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(80% 60% at 50% 20%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 100%)",
        }}
      />

      <main className="relative z-10 px-8 pb-24 pt-24">
        <div className="mx-auto max-w-2xl">
          {TURNSTILE_SITE_KEY && (
            <Script
              src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
              strategy="lazyOnload"
              onLoad={renderCaptcha}
            />
          )}

          <div className="mb-12 text-center">
            <h1 className="text-5xl font-extrabold mb-6">Support</h1>
            <p className="text-lg text-white/90">
              For help, email us at{" "}
              {contactInfo ? (
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-cyan-300 hover:underline"
                >
                  {contactInfo.email}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={handleRevealSupportEmail}
                  className={REVEAL_BUTTON_CLASS}
                  disabled={revealStatus === "loading"}
                >
                  <Image
                    src="/lock.svg"
                    alt=""
                    width={14}
                    height={14}
                    aria-hidden
                    className="icon-white opacity-80"
                  />
                  {revealStatus === "loading"
                    ? "Revealing..."
                    : "click to reveal"}
                  <Image
                    src="/lock_open.svg"
                    alt=""
                    width={14}
                    height={14}
                    aria-hidden
                    className="icon-white opacity-80"
                  />
                </button>
              )}{" "}
              or use the form below.
            </p>
          </div>

          {TURNSTILE_SITE_KEY && (
            <div className="mb-8 flex flex-col items-center">
              <div ref={captchaContainerRef} />
              {captchaError && (
                <p role="alert" className="mt-2 text-center text-xs text-red-300 md:text-sm">
                  {captchaError}
                </p>
              )}
            </div>
          )}

          {revealStatus === "error" && (
            <p role="alert" className="mb-6 text-center text-xs text-red-300 md:text-sm">
              Couldn&apos;t reveal contact info right now. Please use the form
              below.
            </p>
          )}

          <GlassCard className="p-8 !bg-black/60">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="mb-2 flex items-center gap-3 text-sm font-medium"
                >
                  <Image
                    src="/person.svg"
                    alt=""
                    width={20}
                    height={20}
                    aria-hidden
                    className="icon-white"
                  />
                  Your Name
                </label>
                <input
                  id="name"
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-white/40 focus:border-gel-400 focus:outline-none focus:ring-1 focus:ring-gel-400"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="mb-2 flex items-center gap-3 text-sm font-medium"
                >
                  <Image
                    src="/envelope-fill.svg"
                    alt=""
                    width={20}
                    height={20}
                    aria-hidden
                    className="icon-white"
                  />
                  Your Email
                </label>
                <input
                  id="email"
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-white/40 focus:border-gel-400 focus:outline-none focus:ring-1 focus:ring-gel-400"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="message"
                  className="mb-2 flex items-center gap-3 text-sm font-medium"
                >
                  <Image
                    src="/text_ad.svg"
                    alt=""
                    width={20}
                    height={20}
                    aria-hidden
                    className="icon-white"
                  />
                  How can we help you?
                </label>
                <textarea
                  id="message"
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-white/40 focus:border-gel-400 focus:outline-none focus:ring-1 focus:ring-gel-400"
                  name="message"
                  placeholder="Describe your issue or question..."
                  value={form.message}
                  onChange={handleChange}
                  rows={5}
                  required
                />
              </div>
              <button
                type="submit"
                className="flex w-full items-center justify-center gap-3 rounded-full btn-cta px-5 py-3 font-extrabold text-white transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-60"
                style={{ textShadow: "none" }}
                disabled={status === "sending"}
              >
                <Image
                  src="/send-fill.svg"
                  alt=""
                  width={20}
                  height={20}
                  aria-hidden
                  className="icon-white opacity-80"
                />
                {status === "sending" ? "Sending..." : "Send Message"}
              </button>
              <p className="text-center text-xs text-white/65 md:text-sm">
                We typically respond within 24 hours.
              </p>
              <div aria-live="polite" className="text-center">
                {status === "sent" && (
                  <p className="text-green-400">Message sent! We&apos;ll reply soon.</p>
                )}
                {status === "error" && (
                  <p role="alert" className="text-red-400">
                    There was an error. Please try again.
                  </p>
                )}
              </div>
            </form>
          </GlassCard>
        </div>
      </main>
    </div>
  );
}


